import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Chip from '../../components/chip'
import Icon from '../../components/icon'
import ScoreBadge from '../../components/score-badge'
import {
  ATTEMPT_STATUS_LABEL,
  CHALLENGE_TYPE_ICON,
  CHALLENGE_TYPE_LABEL,
  CHALLENGE_TYPES,
  PSYCHOMETRIC_COLORS,
  PSYCHOMETRIC_STATUS,
  PSYCHOMETRIC_TRAIT_LABELS,
  stageStatus
} from '../../utils/constant'
import {
  checkPublicRoute,
  formatDateTime,
  shouldRenderPlaybackIcon
} from '../../utils/utils'
import { PsychometricResultUnavailable } from '../contest/edit/stage/EvaluateAttempt/EvaluateMCQAttempt'

const PsychometricResultsGrid = memo(({ challenge }) => {
  const traits = useMemo(() => {
    return Object.entries(challenge?.traits || {}).map(
      ([key, score], index) => {
        const level = challenge?.traitLevels?.[key]
        return {
          key,
          name: PSYCHOMETRIC_TRAIT_LABELS[key] || key,
          score: score ?? 0,
          subTitle: level?.title || '',
          color: PSYCHOMETRIC_COLORS[index]
        }
      }
    )
  }, [challenge?.traits, challenge?.traitLevels])

  const title = useMemo(
    () => challenge?.title || 'Psychometric Test',
    [challenge?.title]
  )
  const totalQuestions = useMemo(
    () => challenge?.totalQuestions,
    [challenge?.totalQuestions]
  )
  const attemptedQuestions = useMemo(
    () => challenge?.attemptedQuestions,
    [challenge?.attemptedQuestions]
  )
  const status = useMemo(() => challenge?.status, [challenge?.status])

  if (!challenge) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, ml: 1 }}>
        <Typography fontSize="16px" fontWeight={600}>
          {title}
        </Typography>
        {totalQuestions != null && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'var(--grey-100)',
              px: 1.5,
              py: 0.5,
              borderRadius: '6px'
            }}
          >
            <Typography
              fontSize="12px"
              color="var(--grey-600)"
              fontWeight={600}
            >
              Attempted:{' '}
              <Typography
                component="span"
                fontSize="12px"
                fontWeight={800}
                color="var(--grey-800)"
              >
                {attemptedQuestions || 0}/{totalQuestions}
              </Typography>{' '}
              Questions
            </Typography>
          </Box>
        )}
      </Box>
      {status === PSYCHOMETRIC_STATUS.INSUFFICIENT_DATA ? (
        <PsychometricResultUnavailable
          attemptedQuestions={attemptedQuestions}
          totalQuestions={totalQuestions}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            ml: 1
          }}
        >
          {traits.map((trait) => (
            <Box
              key={trait.key}
              sx={{
                width: '180px',
                padding: '16px 8px',
                borderRadius: '8px',
                background: `${trait.color}10`,
                border: `1.5px solid ${trait.color}`,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                justifyContent: 'center'
              }}
            >
              <Typography
                fontSize="12.5px"
                fontWeight={500}
                color="var(--grey-700)"
                sx={{ textTransform: 'capitalize' }}
              >
                {trait.name.toLowerCase()}
              </Typography>
              <Typography
                fontSize="24px"
                fontWeight={800}
                sx={{ color: trait.color, lineHeight: 1 }}
              >
                {trait.score}
                <Typography
                  component="span"
                  fontSize="15px"
                  sx={{ color: 'var(--grey-400)', ml: 0.5, fontWeight: 700 }}
                >
                  /100
                </Typography>
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
})

const CandidateStagesDetail = ({ data, contestId, assessment = false }) => {
  const navigate = useNavigate()

  const isPublicRoute = useMemo(() => {
    return checkPublicRoute()
  }, [])

  const handleViewCode = useCallback(
    (stage) => {
      const path = `${isPublicRoute ? '' : '/'}${
        assessment ? 'assessments' : 'contests'
      }/${contestId}/edit/stage/${stage?.stageId}/${
        stage?.type === CHALLENGE_TYPES.VIDEO_RESPONSE
          ? 'video-response-evaluate'
          : stage?.type === CHALLENGE_TYPES.MCQ
            ? 'mcq-evaluate'
            : stage?.type === CHALLENGE_TYPES.PSYCHOMETRIC
              ? 'psychometric-evaluate'
              : stage?.type === CHALLENGE_TYPES.AI_SCREENING
                ? 'ai-screening-evaluate'
                : 'evaluate'
      }/${stage?.attemptId}`

      navigate(path, {
        state: {
          pathname: `${window.location.pathname?.toLowerCase()}${
            window.location.search
          }`
        }
      })
    },
    [navigate, contestId, isPublicRoute, assessment]
  )

  return (
    <Card sx={{ padding: '24px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {data?.length > 0 && (
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: '700',
              paddingBottom: '10px'
            }}
          >
            Assessment Stages
          </Typography>
        )}

        {data?.length > 0 ? (
          data?.map((stage, index) => (
            <Accordion
              key={stage?.attemptId || index}
              elevation={0}
              square
              sx={{
                border: '1px solid var(--grey-300)',
                boxShadow: 'none',
                borderRadius: '8px',
                '&.MuiAccordion-root': {
                  boxShadow: 'none'
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: '10px', md: '0px' },
                    paddingRight: { xs: '5px', md: '20px' }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Box
                      sx={{
                        padding: '8px',
                        backgroundColor: 'var(--grey-100)',
                        borderRadius: '8px',
                        alignContent: 'center'
                      }}
                    >
                      <Icon
                        icon={
                          CHALLENGE_TYPE_ICON?.[stage?.type] ||
                          'mdi:code-braces'
                        }
                        fontSize={'25px'}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography fontSize={'16px'} fontWeight={800}>
                        {stage?.stage}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    {stage?.linearityScore != null && (
                      <Chip
                        label={`Linearity: ${stage?.linearityScore}%`}
                        variant="outlined"
                        size="medium"
                        color={
                          stage?.linearityScore == null
                            ? 'default'
                            : stage?.linearityScore > 70
                              ? 'error'
                              : stage?.linearityScore > 40
                                ? 'warning'
                                : 'success'
                        }
                      />
                    )}
                    {stage?.aiScore != null && (
                      <Chip
                        label={`AI Score: ${stage?.aiScore}%`}
                        variant="outlined"
                        size="medium"
                        color={
                          stage?.aiScore == null
                            ? 'default'
                            : stage?.aiScore > 70
                              ? 'success'
                              : stage?.aiScore > 40
                                ? 'warning'
                                : 'error'
                        }
                      />
                    )}
                    {stage?.type !== CHALLENGE_TYPES.PSYCHOMETRIC && (
                      <Chip
                        label={`${
                          stage?.score !== null
                            ? `Score: ${stage?.score}/${stage?.outOfScore} `
                            : 'Score: N/A'
                        }`}
                        variant="outlined"
                        size="medium"
                        color={
                          stage?.score === null
                            ? 'default'
                            : stage?.score > 70
                              ? 'success'
                              : stage?.score > 40
                                ? 'warning'
                                : 'error'
                        }
                      />
                    )}

                    {shouldRenderPlaybackIcon(stage?.type, stage?.status) && (
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={
                          <Icon
                            icon="streamline:code-monitor-1-solid"
                            color="inherit"
                            style={{ fontSize: '14px' }}
                          />
                        }
                        onClick={() => handleViewCode(stage)}
                        sx={{
                          bgcolor: 'var(--primary-main)',
                          color: 'var(--primary-contrastText)',
                          '&:hover': {
                            bgcolor: 'var(--primary-dark)'
                          },
                          padding: '7px 10px',
                          borderRadius: '8px'
                        }}
                      >
                        Evaluate
                      </Button>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <Divider sx={{ width: '100%' }} />
              <AccordionDetails sx={{ padding: '0px' }}>
                <Grid
                  container
                  spacing={4}
                  sx={{ padding: '16px 16px 0px 16px' }}
                >
                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Type
                    </Typography>
                    <Chip
                      rounded
                      size="small"
                      label={CHALLENGE_TYPE_LABEL?.[stage?.type] || '-'}
                      color="default"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Status
                    </Typography>
                    <Chip
                      rounded
                      size="small"
                      label={ATTEMPT_STATUS_LABEL?.[stage?.status] || '-'}
                      color={
                        stage?.status === stageStatus?.DISQUALIFY ||
                        stage?.status === stageStatus?.REJECTED
                          ? 'error'
                          : stage?.status === stageStatus?.IN_PROGRESS
                            ? 'warning'
                            : stage?.status === stageStatus?.FINISHED
                              ? 'success'
                              : stage?.status === stageStatus?.NO_SHOW
                                ? 'alert'
                                : 'default'
                      }
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Duration
                    </Typography>
                    <Typography fontSize="15px">
                      {stage?.duration ? `${stage?.duration} mins` : '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color="var(--grey-500)"
                      mb={1}
                    >
                      Start Time
                    </Typography>
                    <Typography fontSize="15px">
                      {stage?.startTimeStamp
                        ? formatDateTime(stage?.startTimeStamp, false, false)
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>

                {stage?.results?.filter((i) => i?.challengeId).length > 0 && (
                  <>
                    <Divider sx={{ width: '100%', margin: '20px 0px' }} />
                    <Typography fontSize="16px" fontWeight={600} mb={1} ml={4}>
                      Challenges ({stage?.results?.length || 0})
                    </Typography>
                  </>
                )}
                {stage?.type !== CHALLENGE_TYPES?.PSYCHOMETRIC &&
                stage?.type !== CHALLENGE_TYPES?.MCQ ? (
                  <Box sx={{ padding: '0px 16px 16px ' }}>
                    {stage?.results?.length > 0 &&
                      stage?.results?.map(
                        (challenge) =>
                          (challenge?.challengeId || challenge?.name) && (
                            <Box
                              sx={{
                                border: '1px solid var(--grey-300)',
                                borderRadius: '8px',
                                marginTop: '10px',
                                padding: '18px'
                              }}
                              key={challenge?.challengeId || challenge?.name}
                            >
                              <Grid container spacing={4}>
                                <Grid item xs={6} sm={3}>
                                  <Typography
                                    fontSize="13px"
                                    fontWeight={600}
                                    color="var(--grey-500)"
                                  >
                                    Challenge Name
                                  </Typography>
                                  <Typography
                                    fontSize="15px"
                                    fontWeight={'600'}
                                  >
                                    {challenge?.challengeName ||
                                      challenge?.name ||
                                      '-'}
                                  </Typography>
                                </Grid>
                                {challenge?.linearityScore != null && (
                                  <Grid item xs={6} sm={2}>
                                    <Typography
                                      fontSize="13px"
                                      fontWeight={600}
                                      color="var(--grey-500)"
                                      mb={1}
                                    >
                                      Linearity
                                    </Typography>
                                    <Typography
                                      fontSize="15px"
                                      fontWeight={'600'}
                                    >
                                      {challenge?.linearityScore}%
                                    </Typography>
                                  </Grid>
                                )}
                                {challenge?.aiScore != null && (
                                  <Grid item xs={6} sm={2}>
                                    <Typography
                                      fontSize="13px"
                                      fontWeight={600}
                                      color="var(--grey-500)"
                                      mb={1}
                                    >
                                      AI Score
                                    </Typography>
                                    <Typography
                                      fontSize="15px"
                                      fontWeight={'600'}
                                    >
                                      {challenge?.aiScore}%
                                    </Typography>
                                  </Grid>
                                )}
                                <Grid
                                  item
                                  xs={6}
                                  sm={
                                    !challenge?.aiScore &&
                                    !challenge?.linearityScore
                                      ? 3
                                      : 2
                                  }
                                >
                                  <Typography
                                    fontSize="13px"
                                    fontWeight={600}
                                    color="var(--grey-500)"
                                    mb={1}
                                  >
                                    Score
                                  </Typography>
                                  <Typography
                                    fontSize="15px"
                                    fontWeight={'600'}
                                  >
                                    {challenge?.score !== null ||
                                    challenge?.acquiredScore !== null
                                      ? ` ${
                                          challenge?.score ??
                                          challenge?.acquiredScore
                                        }/${
                                          challenge?.outOfScore ||
                                          challenge?.totalMark ||
                                          100
                                        }`
                                      : ' N/A'}
                                  </Typography>
                                </Grid>

                                {challenge?.languageLabel && (
                                  <Grid
                                    item
                                    xs={6}
                                    sm={
                                      !challenge?.aiScore &&
                                      !challenge?.linearityScore
                                        ? 3
                                        : 2
                                    }
                                  >
                                    <Typography
                                      fontSize="13px"
                                      fontWeight={600}
                                      color="var(--grey-500)"
                                      mb={1}
                                    >
                                      Language
                                    </Typography>
                                    <Typography
                                      fontSize="15px"
                                      fontWeight={'600'}
                                    >
                                      {challenge?.languageLabel || '-'}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          )
                      )}
                  </Box>
                ) : (
                  <></>
                )}
                {stage?.type === CHALLENGE_TYPES?.MCQ &&
                  stage?.topicWiseMarkBreakDown?.length > 0 && (
                    <>
                      <Divider sx={{ width: '100%', margin: '20px 0px' }} />
                      <Typography
                        fontSize="16px"
                        fontWeight={600}
                        mb={1}
                        ml={4}
                      >
                        Skills ({stage?.topicWiseMarkBreakDown?.length || 0})
                      </Typography>
                    </>
                  )}
                {stage?.type === CHALLENGE_TYPES?.MCQ &&
                  stage?.topicWiseMarkBreakDown?.length > 0 && (
                    <Box sx={{ padding: '0px 16px 16px 16px' }}>
                      <Box
                        sx={{
                          border: '1px solid var(--grey-300)',
                          borderRadius: '8px',
                          marginTop: '10px',
                          padding: '18px'
                        }}
                      >
                        <Grid
                          container
                          spacing={4}
                          alignItems="center"
                          sx={{ display: { xs: 'none', sm: 'flex' }, mb: 2 }}
                        >
                          <Grid item xs={6} sm={2.5}>
                            <Typography
                              fontSize="13px"
                              fontWeight={600}
                              color="var(--grey-500)"
                            >
                              Skill
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2.5}>
                            <Typography
                              fontSize="13px"
                              fontWeight={600}
                              color="var(--grey-500)"
                            >
                              # Questions
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography
                              fontSize="13px"
                              fontWeight={600}
                              color="var(--grey-500)"
                            >
                              Attempted
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2.5}>
                            <Typography
                              fontSize="13px"
                              fontWeight={600}
                              color="var(--grey-500)"
                            >
                              Unattempted
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2.5}>
                            <Typography
                              fontSize="13px"
                              fontWeight={600}
                              color="var(--grey-500)"
                            >
                              Score
                            </Typography>
                          </Grid>
                        </Grid>

                        {stage?.topicWiseMarkBreakDown?.map((topic, idx) => (
                          <Box
                            key={topic?.topicId || idx}
                            sx={{ mt: idx > 0 ? 3 : 0 }}
                          >
                            <Grid container spacing={3} alignItems="center">
                              <Grid item xs={6} sm={2.5}>
                                <Typography
                                  sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'var(--grey-500)',
                                    mb: 1
                                  }}
                                >
                                  Skill
                                </Typography>
                                <Typography fontSize="15px" fontWeight={'600'}>
                                  {topic?.topicName || '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={2.5}>
                                <Typography
                                  sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'var(--grey-500)',
                                    mb: 1
                                  }}
                                >
                                  # Questions
                                </Typography>
                                <Typography fontSize="15px" fontWeight={'600'}>
                                  {topic?.totalQuestions ?? '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={2}>
                                <Typography
                                  sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'var(--grey-500)',
                                    mb: 1
                                  }}
                                >
                                  Attempted
                                </Typography>
                                <Typography fontSize="15px" fontWeight={'600'}>
                                  {topic?.attempted ?? '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={2.5}>
                                <Typography
                                  sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'var(--grey-500)',
                                    mb: 1
                                  }}
                                >
                                  Unattempted
                                </Typography>
                                <Typography fontSize="15px" fontWeight={'600'}>
                                  {topic?.unattempted ?? '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={2.5}>
                                <Typography
                                  sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'var(--grey-500)',
                                    mb: 1
                                  }}
                                >
                                  Score
                                </Typography>
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  {topic?.score == null ? (
                                    <Typography
                                      fontSize="15px"
                                      fontWeight={'600'}
                                    >
                                      -
                                    </Typography>
                                  ) : (
                                    <ScoreBadge
                                      acquired={topic?.score}
                                      total={topic?.totalScore}
                                      sx={{
                                        '& .score-badge': {
                                          margin: '0 !important'
                                        }
                                      }}
                                    />
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                {stage?.type === CHALLENGE_TYPES.PSYCHOMETRIC &&
                  stage?.psychometricReport && (
                    <>
                      <Divider sx={{ width: '100%', margin: '20px 0px' }} />
                      <Box
                        sx={{
                          padding: '0px 16px 16px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4
                        }}
                      >
                        {Object.values(stage?.psychometricReport).map(
                          (challenge, index) => (
                            <PsychometricResultsGrid
                              key={challenge?.title || index}
                              challenge={challenge}
                            />
                          )
                        )}
                      </Box>
                    </>
                  )}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Box
            sx={{
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography>No stage data available</Typography>
          </Box>
        )}
      </Box>
    </Card>
  )
}

export default memo(CandidateStagesDetail)
